/**
 * 読み込み中 / エラー の共通表示（05-ui.md §11.2）。
 *
 * 「スピナー単体は使わない」「403 は権限の文言に差し替える」という規約を
 * 画面ごとに書かせないため、ここに閉じ込める。空状態は内容が画面ごとに
 * 大きく違うので各画面で `<EmptyState>` を書く。
 */
import * as React from 'react';
import { Alert, AlertDescription, AlertTitle, Button, Skeleton } from '@mail-sv/ui';
import { Loader2, RotateCcw } from 'lucide-react';
import { ApiError, errorMessage } from '../lib/api.js';

export function ErrorAlert({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <Alert variant="destructive">
      <AlertTitle>{forbidden ? '権限がありません' : '読み込みに失敗しました'}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          {forbidden
            ? 'この情報を見る権限がありません。組織の管理者にご確認ください。'
            : errorMessage(error)}
        </p>
        {onRetry && !forbidden ? (
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            <RotateCcw aria-hidden />
            再試行
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/** カード内の読み込み中。行数を指定してレイアウトシフトを防ぐ。 */
export function LoadingBlock({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={i === 0 ? 'h-5 w-1/3' : 'h-4 w-full'} />
      ))}
    </div>
  );
}

/** 再取得中の控えめなスピナー。PageHeader の右に置く。 */
export function RefetchingDot({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Loader2 className="size-3.5 animate-spin" aria-hidden />
      更新中
    </span>
  );
}

/**
 * 読み込み / エラー / 本体 の分岐をまとめる。
 * 一覧は `<DataTable>` が 3 態を持っているので、主に詳細・設定画面で使う。
 */
export function Async<T>({
  query,
  children,
  lines,
}: {
  query: { data: T | undefined; isPending: boolean; isError: boolean; error: unknown; refetch: () => void };
  children: (data: T) => React.ReactNode;
  lines?: number;
}) {
  if (query.isPending) return <LoadingBlock lines={lines} />;
  if (query.isError) return <ErrorAlert error={query.error} onRetry={() => query.refetch()} />;
  if (query.data === undefined) return null;
  return <>{children(query.data)}</>;
}
